import clsx from "clsx";
import { useEffect, useRef } from "react";
import type { CSSProperties, FC, HTMLAttributes } from "react";

import { PremadeInfoStatus } from "@dndbeyond/character-rules-engine";
import EyeIcon from "@dndbeyond/fontawesome-cache/svgs/solid/eye.svg";
import { SpinnerIcon } from "@dndbeyond/ttui/components/Icons";
import { Badge } from "@dndbeyond/ttui/lexicon/components/Badge";
import { Button, ButtonProps } from "@dndbeyond/ttui/lexicon/components/Button";
import { Tooltip } from "@dndbeyond/ttui/lexicon/components/Tooltip";
import { useTooltip } from "@dndbeyond/ttui/lexicon/hooks/useTooltip";

import styles from "./styles.module.css";

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  characterLevel: number;
  characterName: string;
  characterId?: number;
  primaryClassName: string;
  speciesName: string;
  longDescription: string;
  sourceName: string;
  imageUrl: string;
  imageAlt: string;
  primaryButtonProps: ButtonProps;
  primaryButtonText?: string;
  isClaimInProgress: boolean;
  previewLink: string;
  onPreviewClick?: () => void;
  rulesetLabel: string;
  publishStatus: PremadeInfoStatus;
}

export const Card: FC<CardProps> = ({
  publishStatus,
  characterLevel,
  characterName,
  characterId,
  primaryClassName,
  speciesName,
  longDescription,
  sourceName,
  rulesetLabel,
  imageUrl,
  imageAlt,
  primaryButtonText = "Choose",
  primaryButtonProps,
  isClaimInProgress,
  previewLink,
  onPreviewClick,
  className,
  ...props
}) => {
  const { anchorProps, tooltipProps } = useTooltip("preview-button-id");
  const descriptionRef = useRef<HTMLParagraphElement>(null);

  // Track the full (unclamped) description height so hover can animate to it
  useEffect(() => {
    const el = descriptionRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;

    const observer = new ResizeObserver(() => {
      el.style.setProperty("--description-height", `${el.scrollHeight}px`);
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [longDescription]);

  // Convert literal \n strings to actual newlines
  const formattedDescription = longDescription.replace(/\\n/g, "\n");

  return (
    <div
      className={clsx([
        styles.card,
        styles[publishStatus.toLowerCase()],
        className,
      ])}
      style={{ "--background-image": `url(${imageUrl})` } as CSSProperties}
      aria-label={imageAlt}
      {...props}
    >
      {publishStatus !== PremadeInfoStatus.PUBLISHED && (
        <Badge
          className={styles.statusBadge}
          borderColor={
            publishStatus === PremadeInfoStatus.DRAFT
              ? "var(--lexicon_magenta-600)"
              : "var(--lexicon_grey-300)"
          }
          label={publishStatus}
        />
      )}
      <div className={styles.gradient} />
      <div className={styles.content}>
        <h2 className={styles.title} id={characterId?.toString()}>
          <span className={styles.eyebrow}>{rulesetLabel}</span>
          {characterName}
        </h2>
        <div className={styles.badgeGroup}>
          <Badge
            className={styles.badge}
            label={`${primaryClassName} Lvl. ${characterLevel}`}
          />
          <Badge className={styles.badge} label={speciesName} />
        </div>

        <p ref={descriptionRef} className={styles.description}>
          {formattedDescription}
        </p>
        <div className={styles.footer}>
          <span className={styles.source}>{sourceName}</span>
          <div className={styles.actions}>
            <>
              <Button
                size="small"
                color="secondary"
                href={previewLink}
                aria-label={`Preview ${characterName}: ${speciesName} ${primaryClassName}`}
                {...anchorProps}
                onClick={onPreviewClick}
              >
                <EyeIcon />
              </Button>
              <Tooltip {...tooltipProps}>Preview character sheet</Tooltip>
            </>
            <Button
              className={styles.claimButton}
              size="small"
              color="primary"
              variant="rightArrow"
              {...primaryButtonProps}
            >
              {isClaimInProgress ? <SpinnerIcon /> : primaryButtonText}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
